
/**
 * Application Configuration
 * Provides environment-based configuration with sensible defaults and validation
 */

// Environment detection - Vite uses import.meta.env
const isDevelopment = import.meta.env.VITE_ENVIRONMENT === 'development';
const isProduction = import.meta.env.VITE_ENVIRONMENT === 'production';
console.log('Environment:', import.meta.env.VITE_ENVIRONMENT);

/**
 * Validates and returns environment variable with optional default
 * @param {string} key - Environment variable key
 * @param {string} defaultValue - Default value if env var is missing
 * @param {boolean} required - Whether this value is required
 * @returns {string}
 */
const getEnvVar = (key, defaultValue = '', required = false) => {
  const value = import.meta.env[key];
  
  if (!value && required) {
    console.error(`❌ Required environment variable ${key} is missing`);
    throw new Error(`Required environment variable ${key} is not set`);
  }
  
  if (!value && defaultValue) {
    if (isDevelopment) {
      console.warn(`⚠️ Environment variable ${key} not set, using default: ${defaultValue}`);
    }
    return defaultValue;
  }
  
  return value || defaultValue;
};

/**
 * Validates URL format
 * @param {string} url - URL to validate
 * @param {string} name - Name for error messages
 * @returns {string}
 */
const validateUrl = (url, name) => {
  if (!url) return url;
  
  try {
    new URL(url);
    return url.endsWith('/') ? url : url + '/';
  } catch (error) {
    console.error(`❌ Invalid URL format for ${name}: ${url}`);
    return url;
  }
};

// Core Application Configuration
export const SITE_NAME = getEnvVar('VITE_SITE_NAME', 'Magipik Administration');

// API Configuration with validation
export const API_URL = validateUrl(
  getEnvVar(
    'VITE_API_SERVER_URL',
    isDevelopment ? 'http://localhost:8000/be/' : 'https://api.magipik.com/be/',
    true
  ),
  'API_URL'
);

export const AUTH_API_URL = validateUrl(
  getEnvVar(
    'VITE_AUTH_API_SERVER_URL',
    isDevelopment ? 'http://localhost:8000/' : 'https://api.magipik.com/',
    true
  ),
  'AUTH_API_URL'
);

export const FILE_URL = validateUrl(
  getEnvVar(
    'VITE_FILE_SEVER_URL',
    isDevelopment ? 'http://127.0.0.1' : 'https://file.magipik.com'
  ),
  'FILE_URL'
);

export const MEDIA_URL = validateUrl(
  getEnvVar(
    'VITE_MEDIA_SEVER_URL',
    isDevelopment ? 'http://localhost/magipik-api/' : 'https://backend.magipik.com/'
  ),
  'MEDIA_URL'
);

export const FE_WEBSITE_URL = validateUrl(
  getEnvVar('VITE_FE_WEBSITE_URL', 'https://magipik.com/'),
  'FE_WEBSITE_URL'
);

export const NODE_URL = validateUrl(
  getEnvVar('VITE_NODE_FILE_URL', 'https://file.magipik.com'),
  'NODE_URL'
);


// AWS S3 Configuration (with validation)
export const S3_CONFIG = {
  accessKey: getEnvVar('VITE_AWS_S3_ACCESS_KEY', '', isProduction),
  secretKey: getEnvVar('VITE_AWS_S3_SECRET_KEY', '', isProduction),
  region: getEnvVar('VITE_AWS_S3_REGION', 'eu-central-1'),
  bucket: getEnvVar('VITE_AWS_S3_BUCKET', 'magipik-res'),
  endpoint: getEnvVar('VITE_AWS_S3_ENDPOINT', ''),
};

// Legacy exports for backward compatibility
export const S3_KEY = S3_CONFIG.accessKey;
export const S3_SECRET = S3_CONFIG.secretKey;
export const S3_REGION = S3_CONFIG.region;
export const S3_BUCKET = S3_CONFIG.bucket;
export const S3_ENDPOINT = S3_CONFIG.endpoint;

// Backblaze B2 Configuration (with validation)
export const B2_BUCKET = getEnvVar('VITE_B2_BUCKET_NAME', '', true);
export const B2_ENDPOINT = getEnvVar('VITE_B2_S3_ENDPOINT', '', true);

// Special keys and tokens
export const SCAN_ALL_KEY = getEnvVar('VITE_SCAN_ALL_KEY', '');
export const API_KEY = getEnvVar('VITE_API_KEY', '', true); // API key for backend authentication

// Always read both keys from env, regardless of provider
export const OPENAI_KEY = getEnvVar('VITE_OPENAI_API_KEY', '', isProduction);
export const GEMINI_KEY = getEnvVar('VITE_GEMINI_API_KEY', '', isProduction);
export const AI_DEFAULT_PROVIDER = getEnvVar('VITE_AI_DEFAULT_PROVIDER', 'gemini');
export const AI_ENABLED = String(getEnvVar('VITE_AI_GENERATED_ENABLED', 'false')).toLowerCase() === 'true';
// Dynamically select the key based on provider at runtime
export const AI_API_KEY = (() => {
  if (AI_DEFAULT_PROVIDER === 'openai') return OPENAI_KEY;
  if (AI_DEFAULT_PROVIDER === 'gemini') return GEMINI_KEY;
  return '';
})();

// Application Constants
export const EXCLUDE_TAGS = [
  "of", "a", "an", "the", "or", "for", "any", "some", "with", "these",
  "to", "your", "you", "that", "this", "types", "type", "best", "use",
  "other", "free", "view", "are", "is", "new", "different", "vector", "and"
];

// Feature flags based on environment
export const FEATURES = {
  enableDebugLogs: isDevelopment,
  enableAnalytics: isProduction,
  enableAI: !!OPENAI_KEY,
  enableS3Upload: !!(S3_CONFIG.accessKey && S3_CONFIG.secretKey),
};

// Configuration validation on startup
export const validateConfiguration = () => {
  const errors = [];
  
  // Check required URLs
  if (!API_URL) errors.push('API_URL is required');
  if (!AUTH_API_URL) errors.push('AUTH_API_URL is required');
  
  // Check production requirements
  if (isProduction) {
    if (!OPENAI_KEY) errors.push('OPENAI_KEY is required in production');
    if (!S3_CONFIG.accessKey) errors.push('S3 credentials are required in production');
  }
  
  // Validate URL formats
  const urls = { API_URL, AUTH_API_URL, FILE_URL, MEDIA_URL, FE_WEBSITE_URL, NODE_URL };
  Object.entries(urls).forEach(([name, url]) => {
    if (url && !url.startsWith('http')) {
      errors.push(`${name} must be a valid HTTP/HTTPS URL`);
    }
  });
  
  if (errors.length > 0) {
    console.error('❌ Configuration validation failed:');
    errors.forEach(error => console.error(`  - ${error}`));
    
    if (isProduction) {
      throw new Error('Configuration validation failed in production');
    }
  } else if (isDevelopment) {
    console.log('✅ Configuration validation passed');
  }
  
  return errors.length === 0;
};

// Auto-validate on import
validateConfiguration();

// Export configuration summary for debugging
export const CONFIG_SUMMARY = {
  environment: import.meta.env.VITE_ENVIRONMENT,
  siteName: SITE_NAME,
  apiUrl: API_URL,
  authApiUrl: AUTH_API_URL,
  hasApiKey: !!API_KEY,
  features: FEATURES,
  hasS3Config: !!(S3_CONFIG.accessKey && S3_CONFIG.secretKey),
  hasOpenAI: !!OPENAI_KEY,
};

// Development helper
if (isDevelopment) {
  console.log('📋 Configuration Summary:', CONFIG_SUMMARY);
}
